/**
 * Webhooks API service
 */

import { apiClient } from './client';

// =============================================================================
// Types
// =============================================================================

export interface Webhook {
  id: string;
  organizationId: string;
  url: string;
  description: string | null;
  events: string[];
  projectIds: string[] | null;
  headers: Record<string, string> | null;
  enabled: boolean;
  maxRetries: number;
  retryDelaySeconds: number;

  // Statistics
  deliveryCount: number;
  successCount: number;
  failureCount: number;
  lastDeliveredAt: string | null;
  lastFailureAt: string | null;
  lastFailureReason: string | null;

  createdAt: string;
  updatedAt: string;
}

export interface WebhookWithSecret extends Webhook {
  secret: string;
}

export interface WebhookDelivery {
  id: string;
  webhookId: string;
  eventType: string;
  eventId: string;
  status: 'pending' | 'sending' | 'delivered' | 'failed' | 'retrying';
  attemptNumber: number;

  // Request details
  requestPayload: Record<string, unknown>;
  requestHeaders: Record<string, string> | null;

  // Response details
  responseStatus: number | null;
  responseBody: string | null;
  responseHeaders: Record<string, string> | null;

  // Error info
  errorMessage: string | null;

  // Timing
  durationMs: number | null;
  deliveredAt: string | null;
  nextRetryAt: string | null;

  createdAt: string;
}

export interface WebhookListResponse {
  webhooks: Webhook[];
  total: number;
}

export interface DeliveryListResponse {
  deliveries: WebhookDelivery[];
  total: number;
}

export interface WebhookEventType {
  type: string;
  description: string;
}

export interface CreateWebhookRequest {
  url: string;
  description?: string;
  events?: string[];
  projectIds?: string[];
  headers?: Record<string, string>;
  maxRetries?: number;
}

export interface UpdateWebhookRequest {
  url?: string;
  description?: string;
  events?: string[];
  projectIds?: string[];
  headers?: Record<string, string>;
  enabled?: boolean;
  maxRetries?: number;
}

// Backend webhook routes serialize snake_case (unlike BaseSchema routes).
type SnakeCase<S extends string> = S extends `${infer C}${infer Rest}`
  ? `${C extends Lowercase<C> ? C : `_${Lowercase<C>}`}${SnakeCase<Rest>}` : S;
type Wire<T> = { [K in keyof T as K extends string ? SnakeCase<K> : K]: T[K] };

function webhookFromWire(data: Wire<Webhook>): Webhook {
  return {
    id: data.id, organizationId: data.organization_id, url: data.url,
    description: data.description, events: data.events, projectIds: data.project_ids,
    headers: data.headers, enabled: data.enabled, maxRetries: data.max_retries,
    retryDelaySeconds: data.retry_delay_seconds, deliveryCount: data.delivery_count,
    successCount: data.success_count, failureCount: data.failure_count,
    lastDeliveredAt: data.last_delivered_at, lastFailureAt: data.last_failure_at,
    lastFailureReason: data.last_failure_reason, createdAt: data.created_at, updatedAt: data.updated_at,
  };
}

function deliveryFromWire(data: Wire<WebhookDelivery>): WebhookDelivery {
  return {
    id: data.id, webhookId: data.webhook_id, eventType: data.event_type, eventId: data.event_id,
    status: data.status, attemptNumber: data.attempt_number, requestPayload: data.request_payload,
    requestHeaders: data.request_headers, responseStatus: data.response_status,
    responseBody: data.response_body, responseHeaders: data.response_headers,
    errorMessage: data.error_message, durationMs: data.duration_ms, deliveredAt: data.delivered_at,
    nextRetryAt: data.next_retry_at, createdAt: data.created_at,
  };
}

function webhookToWire(data: UpdateWebhookRequest): Wire<UpdateWebhookRequest> {
  const { projectIds, maxRetries, ...rest } = data;
  return { ...rest, ...(projectIds !== undefined ? { project_ids: projectIds } : {}),
    ...(maxRetries !== undefined ? { max_retries: maxRetries } : {}) };
}

// =============================================================================
// API Functions
// =============================================================================

export const webhooksApi = {
  /**
   * List available event types
   */
  async listEventTypes(): Promise<WebhookEventType[]> {
    const response = await apiClient.get<{ events: WebhookEventType[] }>('/webhooks/events');
    return response.data.events;
  },

  /**
   * List webhooks for an organization
   */
  async listWebhooks(
    organizationId: string,
    options?: { enabledOnly?: boolean; limit?: number; offset?: number }
  ): Promise<WebhookListResponse> {
    const params = new URLSearchParams();
    if (options?.enabledOnly) params.set('enabled_only', 'true');
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));

    const query = params.toString();
    const response = await apiClient.get<{ webhooks: Wire<Webhook>[]; total: number }>(
      `/organizations/${organizationId}/webhooks${query ? `?${query}` : ''}`
    );
    return { webhooks: response.data.webhooks.map(webhookFromWire), total: response.data.total };
  },

  /**
   * Create a new webhook
   */
  async createWebhook(
    organizationId: string,
    data: CreateWebhookRequest
  ): Promise<WebhookWithSecret> {
    const response = await apiClient.post<Wire<WebhookWithSecret>>(
      `/organizations/${organizationId}/webhooks`,
      webhookToWire(data)
    );
    return { ...webhookFromWire(response.data), secret: response.data.secret };
  },

  /**
   * Get a webhook by ID
   */
  async getWebhook(organizationId: string, webhookId: string): Promise<Webhook> {
    const response = await apiClient.get<Wire<Webhook>>(
      `/organizations/${organizationId}/webhooks/${webhookId}`
    );
    return webhookFromWire(response.data);
  },

  /**
   * Update a webhook
   */
  async updateWebhook(
    organizationId: string,
    webhookId: string,
    data: UpdateWebhookRequest
  ): Promise<Webhook> {
    const response = await apiClient.patch<Wire<Webhook>>(
      `/organizations/${organizationId}/webhooks/${webhookId}`,
      webhookToWire(data)
    );
    return webhookFromWire(response.data);
  },

  /**
   * Delete a webhook
   */
  async deleteWebhook(organizationId: string, webhookId: string): Promise<void> {
    await apiClient.delete(`/organizations/${organizationId}/webhooks/${webhookId}`);
  },

  /**
   * Regenerate webhook secret
   */
  async regenerateSecret(
    organizationId: string,
    webhookId: string
  ): Promise<WebhookWithSecret> {
    const response = await apiClient.post<Wire<WebhookWithSecret>>(
      `/organizations/${organizationId}/webhooks/${webhookId}/regenerate-secret`
    );
    return { ...webhookFromWire(response.data), secret: response.data.secret };
  },

  /**
   * Test a webhook
   */
  async testWebhook(
    organizationId: string,
    webhookId: string
  ): Promise<WebhookDelivery> {
    const response = await apiClient.post<Wire<WebhookDelivery>>(
      `/organizations/${organizationId}/webhooks/${webhookId}/test`
    );
    return deliveryFromWire(response.data);
  },

  /**
   * List deliveries for a webhook
   */
  async listDeliveries(
    organizationId: string,
    webhookId: string,
    options?: { limit?: number; offset?: number }
  ): Promise<DeliveryListResponse> {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));

    const query = params.toString();
    const response = await apiClient.get<{ deliveries: Wire<WebhookDelivery>[]; total: number }>(
      `/organizations/${organizationId}/webhooks/${webhookId}/deliveries${query ? `?${query}` : ''}`
    );
    return { deliveries: response.data.deliveries.map(deliveryFromWire), total: response.data.total };
  },

  /**
   * Get a specific delivery
   */
  async getDelivery(
    organizationId: string,
    deliveryId: string
  ): Promise<WebhookDelivery> {
    const response = await apiClient.get<Wire<WebhookDelivery>>(
      `/organizations/${organizationId}/deliveries/${deliveryId}`
    );
    return deliveryFromWire(response.data);
  },

  /**
   * Retry a failed delivery
   */
  async retryDelivery(
    organizationId: string,
    deliveryId: string
  ): Promise<WebhookDelivery> {
    const response = await apiClient.post<Wire<WebhookDelivery>>(
      `/organizations/${organizationId}/deliveries/${deliveryId}/retry`
    );
    return deliveryFromWire(response.data);
  },
};

export default webhooksApi;
